import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentTemplate, HeaderTemplate, Progress, Stepper, TitleHeader } from 'web-library';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useLocation, useParams } from 'react-router-dom';
import NonNationalExamTypeUuidDisplay from '../NonNationalExamTypeUuidDisplay';
import SpersCandidateDetails from '../../../../modules/examRegistration/components/nonNationalExam/spers/SpersCandidateDetails';
import { useGetNonNationalCandidateDetails } from '../../../../api-refactored/non-national-exam-registration/useGetNonNationalCandidateDetails';
import { useGetMotherTongueLanguages } from '../../../../api-refactored/non-national-exam-registration/useGetMotherTongueLanguages';
import { useGetPassportDisabled } from '../../../../api-refactored/non-national-exam-registration/useGetPassportDisabled';
import VacancyExpiredModal from '../VacancyExpiredModal';
import { NonNationalExamNavigationContext } from '../../../../routes/ExamRoute';
import { NonNexPassportDisableContext } from '../../../../routes/RootRouter';

const useStyles = makeStyles()((theme) => ({
  stepper: {
    position: 'relative',
    [theme.breakpoints.up('xl')]: {
      left: '-10%',
    },
    [theme.breakpoints.between('lg', 'xl')]: {
      left: '-10%',
    },
    [theme.breakpoints.between('md', 'xl')]: {
      left: '-2.5%',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-2.5%',
    },
    [theme.breakpoints.down('md')]: {
      left: '-3%',
    },
  },
}));

interface NonNationalCandidateDetailParams {
  uuid: string;
}

interface NonNationalCandidateDetailLocation {
  examType: string;
  examTitle: string;
  isSupplementary: boolean;
}

interface ParentParticularsPageProps {
  routeSteps: { label: string | JSX.Element; disabled?: boolean }[];
  isOpsAdmin?: boolean;
}

const NonNationalCandidateDetail = ({ routeSteps, isOpsAdmin }: ParentParticularsPageProps) => {
  const { t } = useTranslation();
  const { uuid } = useParams<NonNationalCandidateDetailParams>();
  const { state } = useLocation<NonNationalCandidateDetailLocation>();
  const { classes } = useStyles();
  const examTitle =
    t('exam->>RegisterExam.RegisterForAnExam') +
    (state?.isSupplementary ? ` - ${t('exam->>Instructions.Supplementary')}` : '');
  const [isSuccessCandidateDetails, setIsSuccessCandidateDetails] = useState<boolean>(false);
  const [isSuccessMotherTongueLanguages, setIsSuccessMotherTongueLanguages] = useState<boolean>(
    false
  );
  const [isPassportDisabled, setIsPassportDisabled] = useState<boolean>(false);
  const nonNexPassportDisableContext = useContext(NonNexPassportDisableContext);

  const { data: motherTongueLanguagesData } = useGetMotherTongueLanguages({
    onSuccess: () => {
      setIsSuccessMotherTongueLanguages(true);
    },
  });

  const { data: candidateDetailsData } = useGetNonNationalCandidateDetails({
    uuid,
    onSuccess: () => {
      setIsSuccessCandidateDetails(true);
    },
    onError: () => {
      // Do Nothing
    },
  });

  useGetPassportDisabled({
    onSuccess: (res) => {
      setIsPassportDisabled(true);
      console.log(res);
      nonNexPassportDisableContext.setIsPassportDisabled(res);
    },
  });

  if (isSuccessCandidateDetails && isSuccessMotherTongueLanguages && isPassportDisabled) {
    return (
      <>
        <TitleHeader
          title={isOpsAdmin ? t('exam->>NonNationalExam.ReviewApplication') : examTitle}
        />
        <HeaderTemplate>
          <NonNationalExamTypeUuidDisplay nonExamType={state?.examTitle} enableApi={false} />
          <Stepper className={classes.stepper} activeStep={0} steps={routeSteps} />
        </HeaderTemplate>
        <SpersCandidateDetails
          isOpsAdmin={isOpsAdmin}
          citizenshipType={candidateDetailsData?.citizenshipType ?? ''}
          candidateDetailsData={candidateDetailsData || undefined}
          motherTongueLanguagesData={motherTongueLanguagesData}
        />
        {!isOpsAdmin && <VacancyExpiredModal uuid={uuid} />}
      </>
    );
  }
  return (
    <HeaderTemplate>
      <ContentTemplate transparent continuous>
        <Progress color="secondary" />
      </ContentTemplate>
    </HeaderTemplate>
  );
};

export default NonNationalCandidateDetail;
